/*
 * Copyright (c) Nexign, JSC, 2019-2021.
 */

/**
 *
 *
 * @author Yakov Gushchin
 * @version 1.14.0
 * @since 1.0.1
 */

import { PureComponent } from 'react';

import classNames from 'classnames';
import dropRight from 'lodash/dropRight';
import some from 'lodash/some';

import { icons } from '../../../../assets';
import { formatBalance } from '../../../../utils/formatText';
import { Collapse, CollapsePanel, EllipseText } from '../../index';
import { IChartPieItem, IChartTooltipItem } from './chartPieTypes';
import {
    ChartLegendItemColorStyled,
    ChartLegendItemNameStyled,
    ChartLegendItemPriceStyled,
    ChartLegendItemStyled,
    ChartLegendStyled,
} from './styled/ChartPieStyled';

interface IChartLegendProps {
    currency: string;
    data: IChartPieItem[];
    drop: number;
}

interface IChartLegendState {
    currentTooltipItem: IChartTooltipItem;
    isTooltipVisible: boolean;
}

class ChartLegend extends PureComponent<IChartLegendProps, IChartLegendState> {

    state = {
        currentTooltipItem: {
            color: '',
            name: '',
            percent: '',
            value: '',
            x: 0,
            y: 0,
        },
        isTooltipVisible: false,
    };

    isItemDisable = (item: IChartPieItem): boolean => this.state.isTooltipVisible
        && this.state.currentTooltipItem.name !== item.item;

    renderLegendTitle = (item: IChartPieItem, disable: boolean) => (
        <ChartLegendItemStyled
            className={classNames({
                disable,
                'legend_has-children': !!item.children?.length,
            })}
            key={item.item + item.color + item.value}
        >
            <ChartLegendItemColorStyled style={({ background: item.color })}/>
            <ChartLegendItemNameStyled>
                <EllipseText title={item.item} titleAlign="center">{item.item}</EllipseText>
            </ChartLegendItemNameStyled>
            <ChartLegendItemPriceStyled>
                {`${formatBalance(item.value)} ${this.props.currency}`}
            </ChartLegendItemPriceStyled>
        </ChartLegendItemStyled>
    );

    renderLegend = (item: IChartPieItem) => item.children?.length
        ? <Collapse icon={icons.arrowListDownMedium} key={item.item}>
            <CollapsePanel isOpen={true} titleValue={this.renderLegendTitle(item, this.isItemDisable(item))}>
                {item.children.map(child => this.renderLegendTitle(child, this.isItemDisable(item)))}
            </CollapsePanel>
        </Collapse>
        : this.renderLegendTitle(item, this.isItemDisable(item));

    render() {
        const {
            data,
            drop,
        } = this.props;

        return (
            <ChartLegendStyled className={classNames({
                'legend-with-children': some(data, 'children'),
            })}>
                {dropRight(data, drop).map(this.renderLegend)}
            </ChartLegendStyled>
        );
    }
}

export default ChartLegend;
